import './RoutePreferences.scss'

import {
  HDSButton,
  HDSCheckbox,
  HDSCheckboxGroup,
  HDSIcon,
  HDSRadio,
  HDSRadioGroup,
} from '@here/hds-react-components'
import { RouteWaypoint } from 'data/model/routePlanner'
import { useAppSelector } from 'data/store/hooks'
import { i18n } from 'lib/i18n'
import { fromTransportModesPanelModeToRoutingServiceModes } from 'lib/route-planner/formatters'
import { debounce } from 'lodash'
import React, { useCallback, useState } from 'react'
import { getRoutePreferences, RoutePreferenceKey, updateRoutePreferences } from 'utils/storage'

import { TransportModesPanelMode } from '../TransportModesPanel'

interface RoutePreferencesProps {
  onRoutePreferencesChange: (routeWaypoints: RouteWaypoint[]) => void
  selectedTransportMode: TransportModesPanelMode
}

export const RoutePreferences = ({
  onRoutePreferencesChange,
  selectedTransportMode,
}: RoutePreferencesProps): JSX.Element => {
  const routeWaypoints = useAppSelector(({ routePlanner }) => routePlanner.routeWaypoints)
  const [isExpanded, setIsExpanded] = useState(false)
  const routeServiceMode = fromTransportModesPanelModeToRoutingServiceModes(selectedTransportMode)
  const [routePreferences, setRoutePreferences] = useState<RoutePreferenceKey[]>(
    getRoutePreferences(routeServiceMode)
  )
  const i18nBaseKey = 'route-planner.route-preferences.'

  const showCarPreferences = selectedTransportMode === TransportModesPanelMode.Car
  const showBikePreferences = selectedTransportMode === TransportModesPanelMode.Bike
  const showWalkPreferences = selectedTransportMode === TransportModesPanelMode.Walk

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const debounceRouteUpdate = useCallback(
    debounce((preferences: RoutePreferenceKey[]) => {
      updateRoutePreferences(preferences, routeServiceMode)
      onRoutePreferencesChange(routeWaypoints)
    }, 2000),
    [routeWaypoints]
  )

  const toggleRoutePreference = useCallback(
    (key: RoutePreferenceKey, value?: boolean): void => {
      const newRoutePreferences = [...routePreferences]

      const index = newRoutePreferences.indexOf(key)
      if (index > -1 && value !== true) {
        newRoutePreferences.splice(index, 1)
      } else if (value !== false) {
        newRoutePreferences.push(key)
      }

      setRoutePreferences(newRoutePreferences)
      debounceRouteUpdate(newRoutePreferences)
    },
    [debounceRouteUpdate, routePreferences]
  )

  const isChecked = useCallback(
    (key: RoutePreferenceKey): boolean => routePreferences.includes(key),
    [routePreferences]
  )

  const createCheckBox = (key: RoutePreferenceKey, label: string): JSX.Element => (
    <HDSCheckbox checked={isChecked(key)} onCheck={() => toggleRoutePreference(key)}>
      {label}
    </HDSCheckbox>
  )

  const preferencesCount = isChecked('short')
    ? routePreferences.length - 1
    : routePreferences.length

  if (!showCarPreferences && !showBikePreferences && !showWalkPreferences) return <></>

  return (
    <div className="RoutePreferences__container">
      <div className="RoutePreferences__toggle-container">
        <HDSButton
          variant="link"
          className="RoutePreferences__toggle-button"
          onClick={() => setIsExpanded((isExpanded) => !isExpanded)}
        >
          {i18n(`${i18nBaseKey}title`) + (preferencesCount > 0 ? ` (${preferencesCount})` : '')}
          <HDSIcon
            className={
              'RoutePreferences__chevron-icon ' +
              (isExpanded ? 'RoutePreferences__chevron-rotate' : '')
            }
            name="chevron-down"
          />
        </HDSButton>
      </div>
      {isExpanded && (
        <div className="RoutePreferences__content">
          {showCarPreferences && (
            <div className="RoutePreferences__section">
              <HDSRadioGroup
                value=""
                vertical
                onChange={(e) => toggleRoutePreference('short', Boolean(e.detail.value))}
              >
                <HDSRadio
                  value=""
                  checked={!isChecked('short')}
                  className="RoutePreferences__radio-fastest"
                >
                  {i18n(`${i18nBaseKey}fastest`)}
                </HDSRadio>
                <HDSRadio value="true" checked={isChecked('short')}>
                  {i18n(`${i18nBaseKey}shortest`)}
                </HDSRadio>
              </HDSRadioGroup>
            </div>
          )}
          <div className="RoutePreferences__section">
            <HDSCheckboxGroup label="">
              {showCarPreferences && createCheckBox('uTurns', i18n(`${i18nBaseKey}avoid-uturns`))}
              {createCheckBox('ferry', i18n(`${i18nBaseKey}avoid-ferries`))}
              {showCarPreferences &&
                createCheckBox('controlledAccessHighway', i18n(`${i18nBaseKey}avoid-highways`))}
              {showCarPreferences && createCheckBox('tunnel', i18n(`${i18nBaseKey}avoid-tunnels`))}
              {showCarPreferences &&
                createCheckBox('tollRoad', i18n(`${i18nBaseKey}avoid-toll-roads`))}
              {createCheckBox('dirtRoad', i18n(`${i18nBaseKey}avoid-unpaved-roads`))}
            </HDSCheckboxGroup>
          </div>
        </div>
      )}
    </div>
  )
}
